import type { IResizeParams } from 'pages/Batch/types';
import { isRemoteUrl } from 'pages/Batch/utils';

import { BatchDataStatus } from 'configs/constants';

import type { IBatchData } from 'types/store/index';

import BatchDataService from './BatchDataService';
import type { DataActions } from './types';
import { ActionTypes } from './types';

interface IState {
  data: IBatchData[];
  resizeParams: IResizeParams;
}

function revokeBlobURLs(...args: (string | undefined)[]) {
  args.forEach(x => x && URL.revokeObjectURL(x));
}

function dataReducer(state: IState, action: DataActions): IState {
  switch (action.type) {
    case ActionTypes.UPDATE_STATUSES:
      return {
        ...state,
        data: state.data.map(item => {
          if (!action.itemKeys || action.itemKeys.includes(item.key)) {
            return {
              ...item,
              status: action.skipItemsWithError && item.status === BatchDataStatus.ERROR ? BatchDataStatus.ERROR : action.status,
              statusIndicatorMode: action.statusIndicatorMode,
            };
          }
          return item;
        }),
      };
    case ActionTypes.ADD_DATA:
      return {
        ...state,
        data: [...action.items, ...state.data],
      };
    case ActionTypes.PUT_DATA: {
      if (action.item.pendingResult && isRemoteUrl(action.item.pendingResult.src)) {
        BatchDataService.updateItem(action.item.key, { pendingResult: action.item.pendingResult, status: action.item.status });
      } else if (action.item.status === BatchDataStatus.ERROR && action.item.lastToolAction) {
        BatchDataService.updateItem(action.item.key, { lastToolAction: action.item.lastToolAction, status: action.item.status });
      }
      return {
        ...state,
        data: state.data.map(item => {
          if (action.item.key === item.key) {
            // update the result src only if a new src is available and is not the original one
            const isOutdatedResultSrc = item.result.src !== item.source.src && item.result.src !== action.item.result.src;
            revokeBlobURLs(isOutdatedResultSrc ? item.result.src : '');
            return action.item;
          }
          return item;
        }),
      };
    }
    case ActionTypes.UPDATE_IMAGES_DATA: {
      const { items } = action;
      const data = state.data.map(prevItem => {
        const updatedItem = items.find(item => item.key === prevItem.key);
        return updatedItem
          ? {
              ...prevItem,
              result: updatedItem.result,
              source: updatedItem.source,
            }
          : prevItem;
      });

      return {
        ...state,
        data,
      };
    }
    case ActionTypes.DELETE_DATA:
      return {
        ...state,
        data: state.data.filter(item => {
          if (!action.keys.includes(item.key)) return true;

          revokeBlobURLs(item.result.src, item.source.src);
          return false;
        }),
      };
    case ActionTypes.RESET_DATA:
      return {
        ...state,
        data: state.data.map(item => {
          if (!action.keys.includes(item.key)) return item;

          item.result.src !== item.source.src && revokeBlobURLs(item.result.src);

          return {
            ...item,
            result: item.source,
            isProcessed: false,
          };
        }),
      };
    case ActionTypes.APPLY_PENDING: {
      const { selectedPhotoIds } = action;
      const updatedData = state.data.map(item => ({
        ...item,
        result: item.pendingResult || item.result,
        pendingResult: undefined,
        asyncJob: undefined,
        lastToolAction: undefined,
        isProcessed: item.isProcessed || !!item.pendingResult,
        status: BatchDataStatus.PENDING,
      }));

      // TODO (Remove this side effect from reducer too)
      BatchDataService.putMultipleData(updatedData, selectedPhotoIds);

      return {
        ...state,
        data: updatedData,
      };
    }
    case ActionTypes.DISCARD_PENDING:
      // Persist discarded state so reload doesn't restore cancelled results
      BatchDataService.discardPendingData();

      return {
        ...state,
        data: state.data.map(item => {
          revokeBlobURLs(item.pendingResult?.src);
          return {
            ...item,
            pendingResult: undefined,
            asyncJob: undefined,
            lastToolAction: undefined,
            status: BatchDataStatus.PENDING,
            statusIndicatorMode: 'both',
          };
        }),
      };
    case ActionTypes.DELETE_ALL_DATA:
      state.data.forEach(x => revokeBlobURLs(x.result.src, x.source.src));
      return {
        ...state,
        data: [],
      };
    case ActionTypes.SET_RESIZE_PARAMS:
      return {
        ...state,
        resizeParams: action.params,
      };
    default:
      throw new Error();
  }
}

export default dataReducer;
